import { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import { produtosService } from "./produtosService";

// ─── Estado inicial ───────────────────────────────────────────────────────────

const FORM_INITIAL_STATE = {
  id: null,
  nome: "",
  descricao: "",
  ean: "",
  preco_custo: 0,
  valor_diaria: null,
  estoque: 0,
  ativo: true,
  tipo: "produto",
  marca: "", // ← novo
  imagem_url: null, // ← novo
};

const swalSuccess = (title) =>
  Swal.fire({ icon: "success", title, showConfirmButton: false, timer: 1500 });

const swalError = (message) => Swal.fire("Erro", message, "error");

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useProdutos() {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(FORM_INITIAL_STATE);
  const [filtro, setFiltro] = useState("");

  const fetchProdutos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await produtosService.getAll();
      setProdutos(data);
    } catch {
      swalError("Não foi possível carregar os produtos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProdutos();
  }, [fetchProdutos]);

  const openModal = useCallback((produto = null) => {
    // Se for objeto sem id (chamada de "Novo"), trata como criação
    const isNew = !produto || !produto.id;

    setForm(
      isNew
        ? { ...FORM_INITIAL_STATE, tipo: produto?.tipo ?? "produto" }
        : {
            id: produto.id,
            nome: produto.nome ?? "",
            descricao: produto.descricao ?? "",
            ean: produto.ean ?? "",
            preco_custo: produto.preco_custo ?? 0,
            valor_diaria: produto.valor_diaria ?? null,
            estoque: produto.estoque ?? 0,
            ativo: produto.ativo ?? true,
            tipo: produto.tipo ?? "produto",
            marca: produto.marca ?? "",
            imagem_url: produto.imagem_url ?? null,
          },
    );
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setForm(FORM_INITIAL_STATE);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        if (form.id) {
          await produtosService.update(form.id, form);
          await swalSuccess("Salvo com sucesso!");
        } else {
          await produtosService.create(form);
          await swalSuccess("Criado com sucesso!");
        }
        await fetchProdutos();
        closeModal();
      } catch (err) {
        const message = err.response?.data?.error || "Não foi possível salvar.";
        swalError(message);
      }
    },
    [form, fetchProdutos, closeModal],
  );

  const handleToggleAtivo = useCallback(
    (produto) => {
      const isAtivo = produto.ativo;
      Swal.fire({
        title: isAtivo ? "Desativar?" : "Ativar?",
        text: isAtivo
          ? "O item ficará inativo, mas não será excluído."
          : "O item ficará ativo novamente.",
        icon: isAtivo ? "warning" : "question",
        showCancelButton: true,
        confirmButtonColor: "#2563EB",
        cancelButtonColor: "#64748B",
        confirmButtonText: isAtivo ? "Sim, desativar!" : "Sim, ativar!",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        try {
          if (isAtivo) {
            await produtosService.deactivate(produto.id);
            await swalSuccess("Desativado!");
          } else {
            await produtosService.activate(produto.id);
            await swalSuccess("Ativado!");
          }
          await fetchProdutos();
        } catch {
          swalError(
            `Não foi possível ${isAtivo ? "desativar" : "ativar"} o item.`,
          );
        }
      });
    },
    [fetchProdutos],
  );

  // Filtro textual aplicado aqui apenas para a listagem da tabela.
  // A filtragem por tipo (aba) é feita no componente.
  const produtosFiltrados = useMemo(
    () =>
      produtos.filter((p) =>
        [p.nome, p.descricao, p.ean].some((campo) =>
          campo?.toLowerCase().includes(filtro.toLowerCase()),
        ),
      ),
    [produtos, filtro],
  );

  return {
    produtos: produtosFiltrados,
    loading,
    modalOpen,
    form,
    filtro,
    setFiltro,
    setForm,
    openModal,
    closeModal,
    handleSubmit,
    handleToggleAtivo,
  };
}
